import { useJobMetricsPolling } from "@/hooks/useApi";
import { normalizeState } from "@/pages/pipelineHelpers";
import type { JobMetadata, JobMetrics } from "@/types/api";
import type { FlowState } from "./flowMotion";
import { useFlowPace } from "./useFlowPace";
import { useSeriesRates } from "./useSeriesRates";

const METRICS_POLL_MS = 5000;

/** What the flow card's connectors know of the running pipeline: the
 *  pace into the transform and into the sinks, and the source's queue.
 *
 *  Processed records are acknowledged after the transform and every sink
 *  write, so their rate less the transform's drop rate is what reaches the
 *  sinks. The source read rate comes from the lineage series; until it
 *  answers, the sink-side rate stands in so a flowing line is not drawn
 *  still. Same poll as the Metrics card (one query, shared by key); a
 *  pipeline that is not running has no pace, and its connectors stand
 *  still. */
export function usePipelineFlow(
  jobName: string,
  job: JobMetadata | undefined,
): FlowState {
  const running = isRunning(job);
  const { data: metrics, dataUpdatedAt } = useJobMetricsPolling(
    jobName,
    METRICS_POLL_MS,
    running,
  );
  const processedRate = useFlowPace(
    metrics?.events_processed,
    dataUpdatedAt,
    running,
  );
  const rates = useSeriesRates(jobName, running, job?.started_at);
  const intoSinks = sinkRate(processedRate, rates?.dropped);
  return {
    intoTransform: running ? (rates?.watched ?? intoSinks) : null,
    intoSinks,
    ...queueOf(running ? metrics : undefined),
  };
}

/** The rate reaching the sinks: processed less dropped, never below zero;
 *  null while the pipeline is not running. */
function sinkRate(
  processedRate: number | null,
  droppedRate: number | null | undefined,
): number | null {
  if (processedRate === null) return null;
  return Math.max(0, processedRate - (droppedRate ?? 0));
}

/** The source's queue as the metrics report it; nothing queued when the
 *  pipeline is not running or the stats are missing. */
function queueOf(
  metrics: JobMetrics | undefined,
): Pick<FlowState, "queued" | "blocked"> {
  const stats = metrics?.pipeline_process_stats;
  return {
    queued: stats?.queued ?? 0,
    blocked: stats?.source_blocked ?? false,
  };
}

/** Whether the job reads as running; a job not loaded yet does not. */
function isRunning(job: JobMetadata | undefined): boolean {
  if (job === undefined) return false;
  return normalizeState(job.state) === "Running";
}
